import type { LiveComponent } from '@use-gpu/live';
import type { VirtualDraw } from '../../pass/types';

import { yeet, useMemo } from '@use-gpu/live';
import { bindBundle } from '@use-gpu/shader/wgsl';

import { getNativeColor } from '../../hooks/useNativeColor';
import { getShader } from '../../hooks/useShader';
import { drawCall } from '../../queue/draw-call';
import { getShaderLabel } from '../../pass/util';

import { useLightMaterialContext } from '../../providers/light-material-provider';
import { usePassContext } from '../../providers/pass-provider';

import renderVirtualShaded from '@use-gpu/wgsl/render/vertex/virtual-shaded.wgsl';
import {
  main as renderFragmentShaded,
  mainWithDepth as renderFragmentShadedDepth,
} from '@use-gpu/wgsl/render/fragment/oit-shaded.wgsl';

import { getScissorColor } from '@use-gpu/wgsl/mask/scissor.wgsl';
import { getSSAOSurface } from '@use-gpu/wgsl/instance/surface/ssao-surface.wgsl';
import { sampleSSAO } from '@use-gpu/wgsl/use/ssao.wgsl';

export type OITShadedRenderProps = VirtualDraw;

const LABEL = 'OITShadedRender';

export const OITShadedRender: LiveComponent<OITShadedRenderProps> = (props: OITShadedRenderProps) => {
  const {
    links: {
      getVertex,
      getSurface,
      getLight,
      applyMaterial,
      applyEnvironment,
    },
    defines,
    ...rest
  } = props;

  const {
    targets: {oit, ssao},
    bindGroups: {color: {layout: globalLayout, key: pipelineKey}},
    options: {ssao: ssaoOptions},
  } = usePassContext();

  const {useApplyMaterial, useNoApplyMaterial} = useLightMaterialContext();
  const applyLights = applyMaterial ? useApplyMaterial(applyMaterial) : (useNoApplyMaterial(), null);

  if (!oit) throw new Error("OIT mode used inside a pass without oit={true}")

  const [renderContext] = oit;
  const {colorInput, colorSpace} = renderContext;

  const vertexShader = renderVirtualShaded;
  const fragmentShader = defines?.HAS_DEPTH ? renderFragmentShadedDepth : renderFragmentShaded;
  const hasScissor = defines?.HAS_SCISSOR;

  // Binds links into shader
  const [v, f] = useMemo(() => {
    const links = {
      getVertex,
      getSurface: ssao && ssaoOptions ? getShader(getSSAOSurface, [getSurface, sampleSSAO, ssaoOptions.opacity, ssaoOptions.indirect]) : getSurface,
      getLight: getLight && bindBundle(getLight, {applyLights, applyEnvironment}),
      getScissor: hasScissor ? getScissorColor : null,
      toColorSpace: getNativeColor(colorInput, colorSpace),
    };
    const v = bindBundle(vertexShader, links);
    const f = bindBundle(fragmentShader, links);
    return [v, f];
  }, [vertexShader, fragmentShader, getVertex, getSurface, getLight, applyLights, applyEnvironment, colorInput, colorSpace, hasScissor, ssao, ssaoOptions]);

  // Inline the render fiber
  const call = {
    ...rest,
    vertex: v,
    fragment: f,
    defines,
    renderContext,
    globalLayout,
    pipelineKey,
    label: getShaderLabel([getVertex, getSurface, getLight], LABEL),
  };

  return yeet(drawCall(call));
};
